import { useCallback, useMemo, useRef } from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';

export type AutoSaveStatus = 'idle' | 'typing' | 'saving' | 'saved' | 'error';

// Debounce a value, track its save status, dedupe unchanged saves, and expose a flush for
// "save immediately" (export). Shared by autosaves that need a flush-before-export contract.
export function useDebouncedFieldAutosave<T>(
  key: string,
  delayMs: number,
  autosaveTimeouts: MutableRefObject<{ [key: string]: ReturnType<typeof setTimeout> }>,
  setAutoStatus: Dispatch<SetStateAction<{ [key: string]: AutoSaveStatus }>>,
  persist: (value: T) => Promise<unknown>
) {
  const lastSerializedRef = useRef<string | null>(null);
  const requestIdRef = useRef(0);
  const inFlightRef = useRef<Promise<void> | null>(null);

  const save = useCallback(
    (value: T): Promise<void> => {
      const serialized = JSON.stringify(value);
      if (serialized === lastSerializedRef.current) {
        return inFlightRef.current ?? Promise.resolve();
      }
      lastSerializedRef.current = serialized;

      const requestId = ++requestIdRef.current;
      setAutoStatus((prev) => ({ ...prev, [key]: 'saving' }));
      const promise: Promise<void> = persist(value)
        .then(() => {
          if (requestId !== requestIdRef.current) return;
          setAutoStatus((prev) => ({ ...prev, [key]: 'saved' }));
        })
        .catch((error) => {
          if (requestId === requestIdRef.current) {
            setAutoStatus((prev) => ({ ...prev, [key]: 'error' }));
            // Allow a retry (e.g. a re-attempted export) to actually resave this value instead
            // of being deduped away as "unchanged".
            lastSerializedRef.current = null;
          }
          throw error;
        })
        .finally(() => {
          if (inFlightRef.current === promise) inFlightRef.current = null;
        });
      inFlightRef.current = promise;
      return promise;
    },
    [key, persist, setAutoStatus]
  );

  const schedule = useCallback(
    (getValue: () => T) => {
      setAutoStatus((prev) => ({ ...prev, [key]: 'typing' }));
      clearTimeout(autosaveTimeouts.current[key]);
      autosaveTimeouts.current[key] = setTimeout(() => {
        delete autosaveTimeouts.current[key];
        save(getValue()).catch(() => {});
      }, delayMs);
    },
    [key, delayMs, autosaveTimeouts, setAutoStatus, save]
  );

  const flush = useCallback(
    (getValue: () => T): Promise<void> => {
      clearTimeout(autosaveTimeouts.current[key]);
      delete autosaveTimeouts.current[key];
      return save(getValue());
    },
    [key, autosaveTimeouts, save]
  );

  return useMemo(() => ({ schedule, flush }), [schedule, flush]);
}
